import {
  Accordion,
  AccordionDetails,
  AccordionSummary
} from "@material-ui/core";
import { Check, ReportProblem } from "@material-ui/icons";

import produce from "immer";
import isPlainObject from "is-plain-obj";
import useIsMounted from "ismounted";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { AnySchema, reach } from "yup";
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { useAppLoader } from "../../../../../../../components/loader/app-loader-hook";
import { StepperContext } from "../../../../../../../components/stepper/context";
import {
  AddressAttachmentType,
  IdentityAttachmentType,
} from "../../../../../../../store/common/models";
import { VerificationDTO } from "../../../../../../../store/verification/models/dto/due-diligence";
import { VerificationService } from "../../../../../../../store/verification/service";
import { colours } from "../../../../../../../styles/colours";
import { deepOmitBy } from "../../../../../../../utils/utils";
import { ConfigContext } from "../../../common/config-context";
import { FileAttachmentContext } from "../../../common/pages/kyc-details/file-attachment-context";
import { IdTypeContextProvider } from "../../../common/pages/kyc-details/id-type-context";
import { AddressTypeContextProvider } from "../../../common/pages/kyc-details/proof-of-address-type-context";
import { AttachmentContext } from "../../attachment-context";
import { FormState } from "../../form-context";
import NoKycEntries from "./no-kyc-entries";
import { IndividualKycRequest } from "./single";
import { Box } from "@material-ui/core";
import { size } from "../../../../../../../styles/media";
import { green, grey, red } from "@material-ui/core/colors";

type FormType = VerificationDTO.Requests.DueDiligence.Form.Post.Final.T;

export interface IFinaliseFormProps {
  configId: string;
}

const StyledAccordionDetails = styled(AccordionDetails)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  & > * {
    margin: 0.5em;
  }
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 1em;
  box-shadow: none;
`;

const StyledAccordionSummary = styled(AccordionSummary) <{ error: boolean }>`
  font-size: 0.8em;

  border-radius: 13px;

  ${(props) => (props.error ? `border: 1px solid ${colours.redFailed};` : "")}
  ${(props) =>
    props.error ? `background: #ffe5e5;` : "background: whitesmoke;"}
`;

const Title = styled.h1`
  @media (max-width: ${size.tablet - 1}px) {
    margin-top: 0;
  }
`;

export const KycDetailsForm: FC<IFinaliseFormProps> = () => {
  const isMounted = useIsMounted();
  const loader = useAppLoader();
  const {
    state: { kycToFillIn, dueDiligenceId },
  } = useContext(ConfigContext);
  const { data, setData } = useContext(FormState); // Context to save the data;
  const { setValidation, setIsBackEnabled } = useContext(StepperContext);

  const {
    validation,
  } = VerificationDTO.Requests.DueDiligence.Form.Post.Final.useValidations(
    dueDiligenceId
  );

  // We are not doing a global form to improve performance.
  const resolver = useMemo(() => validation.pick(["kycRequests"]).required(), [
    validation,
  ]);

  const [submitted, setSubmitted] = useState<Array<boolean>>(Array(kycToFillIn).fill(false));

  // Hold state to keep if the KYC Request had the required files uploaded
  const [filesUploaded, setFilesUploaded] = useState<Array<boolean>>(
    Array(kycToFillIn).fill(false)
  );
  useEffect(() => setFilesUploaded(Array(kycToFillIn).fill(false)), [
    kycToFillIn,
  ]);

  // Hold state for errors
  const [errors, setErrors] = useState<Array<boolean>>(
    Array(kycToFillIn).fill(false)
  );
  useEffect(() => setErrors(Array(kycToFillIn).fill(false)), [kycToFillIn]);

  /**
   * Set up the validation for this step. Here we do the final call.
   */
  useEffect(() => {
    setValidation(() => async () => {
      setSubmitted(Array(kycToFillIn).fill(true));
      if (dueDiligenceId === undefined) throw new Error("");
      try {
        if (!filesUploaded.every((x) => x)) {
          throw new Error("Attachments missing");
        }

        const singleKYCSchema: AnySchema = reach(resolver, "kycRequests[0]");
        data.kycRequests?.forEach(async (kyc, index) => {
          try {
            await singleKYCSchema.validate(kyc);
            setErrors((curr) => {
              const foo = [...curr];
              foo[index] = false;
              return foo;
            });
          } catch (e: any) {
            setErrors((curr) => {
              const foo = [...curr];
              foo[index] = true;
              return foo;
            });
          }
        });

        const foo = deepOmitBy(
          await resolver.validate(data),
          (val) =>
            val === "" ||
            val === null ||
            (isPlainObject(val) && Object.keys(val).length < 1)
        );
        await loader(
          VerificationService.postDueDiligenceFormFinal(
            dueDiligenceId,
            foo as FormType
          )
        );
      } catch (e: any) {
        throw e;
      }
    });

    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    filesUploaded,
    setValidation,
    isMounted.current,
    resolver,
    setSubmitted,
    data,
    loader,
    dueDiligenceId,
  ]);

  useEffect(() => {
    setIsBackEnabled(true);
  }, [setIsBackEnabled]);

  const [idTypes, setIdTypes] = useState<Array<IdentityAttachmentType>>([]);
  const [addressTypes, setAddressTypes] = useState<
    Array<AddressAttachmentType>
  >([]);

  const { kycs } = useContext(AttachmentContext);
  const [kycIndex, setKycIndex] = useState<
    number
  >(0);

  return (
    <>
      {useMemo(
        () =>

          kycToFillIn > 0 ? (
            <>
              <Box component='div' sx={{ bgcolor: 'rgb(240, 248, 255)', borderRadius: '5px', padding: '1.4em' }}>
                <Grid container >
                  {Array(kycToFillIn).length > 1 && (
                    <Grid item xs={3}>
                      <Box sx={{ paddingRight: '5px' }}>
                        <List>
                          {Array(kycToFillIn).fill(0).map((_, index) => (
                            <ListItem button onClick={() => setKycIndex(index)} selected={index === kycIndex}>
                              <ListItemIcon>
                                {submitted[index] && (errors[index] || !filesUploaded[index]) ? <ReportProblem style={{ fontSize: 25, color: red[500] }} /> : (submitted[index] && Object.keys(data?.kycRequests?.[index] || {}).length > 0) ? <Check style={{ fontSize: 25, color: green[500] }} /> : <ReportProblem style={{ fontSize: 25, color: grey[500] }} />}
                              </ListItemIcon>
                              <ListItemText primary={`KYC ${index + 1}`} secondary={`${typeof data?.kycRequests?.[index]?.userDetails !== 'undefined' ? data?.kycRequests?.[index]?.userDetails?.firstName + ' ' + data?.kycRequests?.[index]?.userDetails?.lastName : ''}`} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Grid>)}
                  <Grid item xs={Array(kycToFillIn).length === 1 ? 12 : 9}>
                    {Array(kycToFillIn).fill(0).map((_, index) => (
                      <>
                        {index == kycIndex && (
                          <Box>
                            {Array(kycToFillIn).length > 1 && <Title>
                              KYC {index + 1}
                            </Title>}
                            <IdTypeContextProvider
                              setIdType={(d) =>
                                setIdTypes(
                                  produce((x) => {
                                    x[index] = d;
                                  })
                                )
                              }
                            >
                              <AddressTypeContextProvider
                                addressType={addressTypes[index]}
                                setAddressType={(d) =>
                                  setAddressTypes(
                                    produce((x) => {
                                      x[index] = d;
                                    })
                                  )
                                }
                              >
                                <FileAttachmentContext.Provider
                                  value={{
                                    setFile: (type, file) =>
                                      kycs.setFile(type, file, index),
                                    files: kycs.files[index] || {},
                                    emailAddress:
                                      data.kycRequests?.[index]?.userDetails
                                        ?.emailAddress,
                                  }}
                                >
                                  <>

                                    <IndividualKycRequest
                                      setFilesUploaded={(b: boolean) => {
                                        if (filesUploaded[index] !== b) {
                                          const val = filesUploaded;
                                          filesUploaded[index] = b;
                                          setFilesUploaded(val);
                                        }
                                      }}
                                      filesUploaded={filesUploaded[index]}
                                      setData={(d) => {
                                        const foo = [...(data.kycRequests || [])];
                                        foo[index] = { ...d };
                                        setData({
                                          kycRequests: foo,
                                        });
                                      }}
                                      data={data.kycRequests?.[index] || {}}
                                      index={index}
                                      numberOfKycs={kycToFillIn}
                                      goToNextKyc={() => {
                                        setKycIndex(index + 1);
                                        const temp = [...submitted];
                                        temp[index] = true;
                                        setSubmitted(temp);
                                      }}
                                    />
                                  </>
                                </FileAttachmentContext.Provider>
                              </AddressTypeContextProvider>
                            </IdTypeContextProvider>
                          </Box>)}
                      </>
                    ))}
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            <NoKycEntries />
          ),
        [
          kycToFillIn,
          addressTypes,
          setIdTypes,
          setAddressTypes,
          idTypes,
          kycs.files,
          kycs.setFile,
          submitted,
          data,
          setData,
          filesUploaded,
          errors,
          setFilesUploaded,
          kycIndex
        ]
      )}
    </>
  );
};
